import { Component, computed, ElementRef, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { MelloLogoComponent, type MelloLogoMode } from '../mello-logo/mello-logo';
import { ColorScheme, ThemeService } from '../theme-switch/theme.service';

export interface HeaderNavItem {
  label: string;
  route: string;
  icon?: string;
  /** Optional count shown at the trailing edge of the nav row. */
  badge?: string | number;
  allowedRoles?: string[];
  children?: HeaderNavItem[];
  exact?: boolean;
  disabledReason?: string;
  /** Product area section used to render visual separators (e.g. Administration). */
  section?: 'primary' | 'administration';
  /** Sidebar section header label; a header renders where the group changes. */
  navGroup?: string;
}

export interface HeaderSearchSuggestion {
  label: string;
  description: string;
  route: string;
}

export type HeaderVariant = 'default' | 'primary';
export type AccountPopoverTab = 'profile' | 'notifications';

@Component({
  selector: 'header[ui]',
  standalone: true,
  imports: [MelloLogoComponent],
  templateUrl: './header.html',
  styleUrls: ['./header.css', './header-account.css'],
  host: {
    '[attr.data-variant]': 'variant',
    '[attr.aria-label]': 'ariaLabel',
    '(document:keydown.escape)': 'onEscape()',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class Header {
  @Input() variant: HeaderVariant = 'default';
  @Input() ariaLabel = 'Primary';
  @Input() userName = '';
  @Input() userEmail = '';
  @Input() userRole = '';
  @Input() userInitials = '';
  @Input() sidebarOpen = false;
  @Input() themeToggle = true;
  @Input() logoMode: MelloLogoMode = 'dark';
  @Input() brandCompact = false;
  @Input() brandAlt = 'Mello 2.0';
  @Input() avatarSrc = '';

  @Input() toggleSidebarAriaLabel = 'Toggle sidebar';
  @Input() sidebarControlsId = 'sidebar';
  @Input() searchPlaceholder = 'Search agents, transcripts...';
  @Input() searchAriaLabel = 'Search agents and transcripts';
  @Input() searchSuggestions: ReadonlyArray<HeaderSearchSuggestion> = [];
  @Input() searchEmptyLabel = 'No matching pages';
  @Input() searchListLabel = 'Suggested pages';
  @Input() closeSearchAriaLabel = 'Close search';
  @Input() userMenuAriaLabel = 'User menu';
  @Input() accountTabLabel = 'Profile';
  @Input() notificationsTabLabel = 'Notifications';
  @Input() accountMenuAriaLabel = 'Account menu';
  @Input() signOutLabel = 'Sign out';
  @Input() profilePreferencesLabel = 'Profile & preferences';
  @Input() helpSupportLabel = 'Help & support';
  @Input() notificationUnread = 0;
  @Input() role = '';
  @Input() roleOptions: ReadonlyArray<{ value: string; label: string }> = [];
  @Input() viewingAsLabel = 'Viewing as';

  @Output() menuToggle = new EventEmitter<void>();
  @Output() signOut = new EventEmitter<void>();
  @Output() roleChange = new EventEmitter<string>();
  @Output() searchSubmitted = new EventEmitter<string>();
  @Output() suggestionSelected = new EventEmitter<HeaderSearchSuggestion>();

  protected readonly theme = inject(ThemeService, { optional: true });
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly query = signal('');
  protected readonly suggestOpen = signal(false);
  protected readonly activeIndex = signal(-1);
  protected readonly matches = computed(() => {
    const query = this.query().toLowerCase().trim();
    const terms = query.split(/\s+/).filter(Boolean);
    if (!terms.length) return [];
    const direct = this.searchSuggestions
      .map((item) => {
        const label = item.label.toLowerCase();
        const haystack = `${label} ${item.description} ${item.route}`.toLowerCase();
        if (!terms.every((term) => haystack.includes(term))) return null;
        const score = terms.reduce((n, term) => n + (label.startsWith(term) ? 3 : label.includes(term) ? 2 : 1), 0);
        return { item, score };
      })
      .filter((entry): entry is { item: HeaderSearchSuggestion; score: number } => entry !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, 7)
      .map((entry) => entry.item);
    if (direct.length) return direct;
    // Nothing contains the text: fall back to the closest pages so search never dead-ends.
    const ranked = this.searchSuggestions
      .map((item) => ({ item, score: closeness(terms, `${item.label} ${item.description} ${item.route}`) }))
      .sort((a, b) => b.score - a.score);
    // Nothing resembles the text: offer the primary pages in their natural order (Command Center first).
    if (!ranked.length || ranked[0].score < 0.45) return this.searchSuggestions.slice(0, 3);
    return ranked.slice(0, 3).map((entry) => entry.item);
  });
  protected readonly accountOpen = signal(false);
  protected readonly accountTab = signal<AccountPopoverTab>('profile');
  protected readonly profileTabId: AccountPopoverTab = 'profile';
  protected readonly notificationsTabId: AccountPopoverTab = 'notifications';
  protected readonly profileTabDomId = 'account-tab-profile';
  protected readonly notificationsTabDomId = 'account-tab-notifications';
  protected readonly searchOpen = signal(
    typeof matchMedia === 'function' ? !matchMedia('(max-width: 760px)').matches : true,
  );
  protected readonly themeToggleLabel = computed(
    () => `Switch to ${this.theme?.inactiveTheme() ?? 'dark'} mode`,
  );
  protected readonly themePressed = computed(
    () => this.theme?.colorScheme() === ColorScheme.Dark,
  );

  protected notificationBadgeText(): string {
    const count = Math.max(0, this.notificationUnread);
    return count > 99 ? '99+' : String(count);
  }

  protected notificationBadgeLabel(): string {
    const count = Math.max(0, this.notificationUnread);
    return `${count} unread notification${count === 1 ? '' : 's'}`;
  }

  protected submitSearch(value: string): void {
    const query = value.trim();
    if (query) {
      this.searchSubmitted.emit(query);
    }
  }

  protected onSearchInput(value: string): void {
    this.query.set(value);
    this.activeIndex.set(-1);
    this.suggestOpen.set(value.trim().length > 0);
  }

  protected onSearchKey(event: KeyboardEvent, input: HTMLInputElement): void {
    const count = this.matches().length;
    switch (event.key) {
      case 'ArrowDown':
        if (!count) return;
        event.preventDefault();
        this.suggestOpen.set(true);
        this.activeIndex.update((index) => (index + 1) % count);
        break;
      case 'ArrowUp':
        if (!count) return;
        event.preventDefault();
        this.activeIndex.update((index) => (index <= 0 ? count - 1 : index - 1));
        break;
      case 'Escape':
        this.suggestOpen.set(false);
        this.activeIndex.set(-1);
        break;
      case 'Enter': {
        const matches = this.matches();
        const active = matches[this.activeIndex()] ?? matches[0];
        if (active) {
          event.preventDefault();
          this.chooseSuggestion(active, input);
        } else {
          this.submitSearch(input.value);
          this.suggestOpen.set(false);
        }
        break;
      }
    }
  }

  protected chooseSuggestion(item: HeaderSearchSuggestion, input: HTMLInputElement): void {
    input.value = '';
    this.query.set('');
    this.suggestOpen.set(false);
    this.activeIndex.set(-1);
    this.suggestionSelected.emit(item);
  }

  protected toggleSearch(): void {
    this.searchOpen.update((open) => !open);
  }

  protected closeSearch(): void {
    this.searchOpen.set(false);
  }

  toggleAccount(): void {
    this.accountOpen.update((open) => !open);
    if (this.accountOpen()) this.accountTab.set('profile');
  }

  closeAccount(): void {
    this.accountOpen.set(false);
  }

  selectAccountTab(tab: AccountPopoverTab): void {
    this.accountTab.set(tab);
  }

  onAccountTabKeydown(event: KeyboardEvent, currentTab: AccountPopoverTab): void {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const tabs = this.accountTabs;
    const index = tabs.findIndex((t) => t.id === currentTab);
    if (index < 0) return;
    let nextIndex = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const next = tabs[nextIndex];
    this.accountTab.set(next.id);
    this.hostElement.nativeElement
      .querySelector<HTMLButtonElement>(`[data-account-popover-tab="${next.id}"]`)
      ?.focus();
  }

  protected get accountTabs(): ReadonlyArray<{
    id: AccountPopoverTab;
    label: string;
    tabId: string;
    panelId: string;
  }> {
    return [
      {
        id: this.profileTabId,
        label: this.accountTabLabel,
        tabId: this.profileTabDomId,
        panelId: 'account-profile-panel',
      },
      {
        id: this.notificationsTabId,
        label: this.notificationsTabLabel,
        tabId: this.notificationsTabDomId,
        panelId: 'account-notifications-panel',
      },
    ];
  }

  protected selectRole(role: string): void {
    this.closeAccount();
    this.roleChange.emit(role);
  }

  protected onSignOut(): void {
    this.closeAccount();
    this.signOut.emit();
  }

  toggleColorScheme(): void {
    this.theme?.toggleColorScheme();
  }

  onEscape(): void {
    if (this.accountOpen()) {
      this.accountOpen.set(false);
      this.hostElement.nativeElement
        .querySelector<HTMLElement>('[data-account-trigger]')
        ?.focus();
    }
  }

  onDocumentClick(event: Event): void {
    const target = event.target;
    if (
      this.accountOpen() &&
      target instanceof Element &&
      !target.closest('[data-account-wrap]')
    ) {
      this.accountOpen.set(false);
    }
  }
}

function editDistance(a: string, b: string): number {
  const row = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i++) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const current = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = current;
    }
  }
  return row[b.length];
}

/** Average best per-term similarity (0-1) of the typed terms against the words in a page's text. */
function closeness(terms: readonly string[], text: string): number {
  const words = text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  if (!words.length) return 0;
  const total = terms.reduce((sum, term) => {
    const best = words.reduce((top, word) => {
      const candidate = word.slice(0, term.length + 2);
      const prefix = word.startsWith(term.slice(0, 3)) ? 0.15 : 0;
      return Math.max(top, 1 - editDistance(term, candidate) / Math.max(term.length, candidate.length), prefix);
    }, 0);
    return sum + best;
  }, 0);
  return total / terms.length;
}
